import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import { Localization } from "@mui/material/locale";
import { Shadows } from "@mui/material/styles/shadows";
import { localeDirections } from "@contexts/locale/conf";
import * as colors from "./colors";
import * as types from "./types";
import "typeface-nunito-sans";
import "./styles.css";

declare module "@mui/material/styles" {
  interface SimplePaletteColorOptions {
    shade?: string;
  }

  interface PaletteColor {
    main: string;
    dark: string;
    light: string;
    contrastText: string;
    shade?: string;
  }
  interface Palette {
    tertiary: Palette["primary"];
    context: types.IContext;
  }
  interface PaletteOptions {
    tertiary: PaletteOptions["primary"];
    context: types.IContext;
  }
  interface TypographyVariants {
    caption1: React.CSSProperties;
    caption2: React.CSSProperties;
    subtitle3: React.CSSProperties;
    code: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    caption1?: React.CSSProperties;
    caption2?: React.CSSProperties;
    caption3?: React.CSSProperties;
    subtitle3?: React.CSSProperties;
    code?: React.CSSProperties;
  }
  interface BreakpointOverrides {
    xs: true; // removes the `xs` breakpoint
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xx: true;
  }
}
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    caption1: true;
    caption2: true;
    caption: false;
    subtitle3: true;
    code: true;
  }
}

let shadows: Shadows | undefined = [...(Array(25).fill("none") as Shadows)];
shadows[0] = "none";
shadows[1] = "0px 0px 2px 1px rgba(0, 0, 0, 0.2)";
shadows[2] = "1px 4px 12px 1px rgba(0, 0, 0, 0.2)";
shadows[3] = "2px 6px 10px 1px rgba(0, 0, 0, 0.2)";
shadows[4] = "3px 8px 12px 1px rgba(0, 0, 0, 0.25)";
shadows[5] = "4px 10px 16px 1.5px rgba(0, 0, 0, 0.25)";
shadows[6] = "6px 12px 18px 0.5px rgba(0, 0, 0, 0.5)";

const theme = (locale: Localization, direction: localeDirections) =>
  createTheme(
    {
      direction: direction,
      spacing: 2,
      palette: {
        mode: "light",
        primary: colors.primary,
        secondary: colors.secondary,
        tertiary: colors.tertiary,
        error: colors.error,
        info: colors.info,
        success: colors.success,
        warning: colors.warning,
        context: colors.context,
        grey: {
          ...colors.grey,
        },
        background: {
          ...colors.background,
        },
      },
      typography: {
        fontFamily:
          direction === "rtl"
            ? "iranyekan, Nunito Sans, sans-serif"
            : "Nunito Sans, iranyekan, sans-serif",
        h1: {
          fontSize: 42,
          fontWeight: 800,
          letterSpacing: "-0.005em",
          fontStyle: "normal",
        },
        h2: {
          fontSize: 38,
          fontWeight: 800,
          fontStyle: "normal",
          letterSpacing: "-0.005em",
        },
        h3: {
          fontSize: 32,
          fontWeight: 800,
          fontStyle: "normal",
        },
        h4: {
          fontSize: 28,
          fontWeight: "bold",
          fontStyle: "normal",
          letterSpacing: "0.0025em",
        },
        h5: {
          fontSize: 24,
          fontWeight: "normal",
          fontStyle: "normal",
        },
        h6: {
          fontSize: 20,
          fontWeight: 600,
          fontStyle: "normal",
        },
        subtitle1: {
          fontSize: 20,
          fontWeight: "bold",
          fontStyle: "normal",
          letterSpacing: "0.0015em",
        },
        subtitle2: {
          fontSize: 16,
          fontWeight: 600,
          fontStyle: "normal",
          letterSpacing: "0.001em",
        },
        subtitle3: {
          fontSize: 14,
          fontWeight: 700,
          fontStyle: "normal",
          letterSpacing: "0.001em",
          lineHeight: 1.188,
        },
        body1: {
          fontSize: 14,
          fontWeight: 400,
          fontStyle: "normal",
          letterSpacing: "0.005em",
        },
        body2: {
          fontSize: 12,
          fontWeight: 400,
          fontStyle: "normal",
          letterSpacing: "0.005em",
        },
        button: {
          fontSize: 14,
          fontWeight: 600,
          fontStyle: "normal",
          letterSpacing: "0.0125em",
        },
        caption1: {
          fontSize: 10,
          fontWeight: 400,
          fontStyle: "normal",
          letterSpacing: "0.0125em",
        },
        caption2: {
          fontSize: 10,
          fontWeight: 300,
          fontStyle: "normal",
          letterSpacing: "0.004em",
        },
        code: {
          fontSize: 10,
          fontWeight: 600,
          fontStyle: "normal",
          letterSpacing: "0.0125em",
          padding: "1px 3px",
          borderRadius: 5,
        },
        overline: {
          fontSize: 6,
          fontWeight: 200,
          fontStyle: "normal",
          letterSpacing: "0.004em",
        },
      },
      breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 900,
          lg: 1200,
          xl: 1536,
          xx: 2550,
        },
      },
      shape: {
        borderRadius: 4,
      },
      components: {
        MuiFormControlLabel: {
          styleOverrides: {
            root: {
              margin: 0,
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              padding: "5px",
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: {
              fontSize: "18px",
              fontWeight: "bold",
              padding: "16px",
            },
          },
        },
        MuiDialogContent: {
          styleOverrides: {
            root: {
              padding: "0 16px",
            },
          },
        },
        MuiDialogActions: {
          styleOverrides: {
            root: {
              padding: "16px",
            },
          },
        },
        MuiButton: {
          defaultProps: {
            disableElevation: true,
          },
          styleOverrides: {
            startIcon: {
              marginLeft: 4,
              "& svg": {
                fontSize: "17px !important",
              },
            },
            sizeSmall: {
              fontSize: "13px",
              fontWeight: 500,
              lineHeight: "22px",
              padding: "4px 10px",
              letterSpacing: "0.46px",
              textTransform: "capitalize",
            },
            containedInherit: {
              color: colors.grey[700] + "!important",
              "&:hover": {
                color: colors.grey[600] + "!important",
              },
            },
            text: {
              color: "#595D6B !important",
              ":disabled": {
                color: "#979BA5 !important",
              },
            },

            sizeMedium: {
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "24px",
              padding: "6px 16px",
              letterSpacing: "0.46px",
              textTransform: "capitalize",
            },
            sizeLarge: {
              fontSize: "15px",
              fontWeight: 500,
              lineHeight: "26px",
              padding: "8px 22px",
              letterSpacing: "0.46px",
              textTransform: "capitalize",
            },
          },
        },
        MuiSvgIcon: {
          defaultProps: {
            fontSize: "small",
          },
          styleOverrides: {
            fontSizeSmall: {
              fontSize: "17px !important",
            },
          },
        },
        MuiListItemText: {
          styleOverrides: {
            root: { textAlign: "start" },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              background: colors.background.paper,
              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderWidth: "2px !important",
              },
            },
            input: { height: "0.3em" },
          },
        },
      },
      shadows,
    },
    locale
  );

export const themeGenerator = (
  locale: Localization,
  direction: localeDirections
) => responsiveFontSizes(theme(locale, direction));
